import React, { useEffect, useState } from 'react';
import {
    DATA_CONFIGURATION,
    DEFAULT_VALUE,
    ENTER,
    INCREMENT,
    LABEL,
} from '../ISRConstants';
import {
    TConnector,
    TField,
    TFieldDataConfiguration, TIndexState,
    TProductCategory,
    TSubmodel,
    TUpsertISRProjectDataModelsInput
} from '../Pages/DefineModel/model';
import { useISRContext } from '../Hooks/useISRContext';
import { getAllProjectClass } from '../Pages/DefineModel/ModelServices/ModalServices';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import ConfigurationFilterComponent from './ConfigurationFilterComponent';

type RangeConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
    projectId?: string | null | undefined;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
}

const RangeConfiguration = ({id, selectedItem, updateFieldDataConfiguration, projectId, userProjectDataModels, indexInfo}: RangeConfigurationProps) => {
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>({});
    const isrContext = useISRContext();

    useEffect(() => {
        const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : {}
        setFieldDataConfiguration(existingValue);
    }, [selectedItem]);

    useEffect(() => {
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        if (hasChanges) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);

    const handleIncrementChange = (value: number) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, increment: value}));
    };

    const handleLabelChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, label: value}));
    };

    const handleDefaultValueChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue: value.length > 0 ? Number(value): value}));
    };

    // const handleAnalyticChange = (value: boolean) => {
    //     setFieldDataConfiguration((prevConfig) => ({...prevConfig, enableAnalytic: value}));
    // }

    useEffect(() => {
        fetchAllClasses();
    }, []);

    const fetchAllClasses = async () => {
        try {
            const responseData = await getAllProjectClass(projectId ?? '');
            if (responseData?.done) {
                isrContext?.setFieldClasses(responseData?.data?.fieldClasses ?? []);
                isrContext?.setModelClasses(responseData?.data?.modelClasses ?? []);
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    return (
        <Card id={id} data-testid={id} title={DATA_CONFIGURATION}>
            <div id={`${id}-settings-section`} className='tw-flex tw-items-start tw-gap-5'>
                <TextField
                    id={`${id}-increment-input`}
                    data-testid={`${id}-increment-input`}
                    label={INCREMENT}
                    type='number'
                    placeholder={ENTER}
                    value={String(fieldDataConfiguration?.increment ?? '')}
                    onChange={(value) => handleIncrementChange(Number(value))}
                    className='tw-w-[15%]'
                />
                <TextField
                    id={`${id}-label-input`}
                    data-testid={`${id}-label-input`}
                    label={LABEL}
                    placeholder={ENTER}
                    value={fieldDataConfiguration?.label ?? ''}
                    onChange={handleLabelChange}
                    className='tw-w-[30%]'
                />
                <TextField
                    id={`${id}-default-value-input`}
                    data-testid={`${id}-default-value-input`}
                    label={DEFAULT_VALUE}
                    type='number'
                    placeholder={ENTER}
                    value={String(fieldDataConfiguration?.defaultValue ?? '')}
                    /* Passes the RAW string on, not Number(value): the handler keeps
                       an empty string as an empty string and only coerces a non-empty
                       one, which is how "no default" stays distinct from 0. */
                    onChange={handleDefaultValueChange}
                    className='tw-w-[15%]'
                />
            </div>

            <ConfigurationFilterComponent
                id={`${id}-config-filter`}
                fieldDataConfiguration={fieldDataConfiguration}
                setFieldDataConfiguration={setFieldDataConfiguration}
                userProjectDataModels={userProjectDataModels}
                indexInfo={indexInfo}
                isRangeField={true}
            />
        </Card>
    );
}

export default RangeConfiguration;